import React, { useState, useEffect } from 'react'
import { ChevronLeft, X, Info } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

const ShopifyCheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const [showSimulationPopup, setShowSimulationPopup] = useState(false)
  const [showFlowHint, setShowFlowHint] = useState(true)
  const [formData, setFormData] = useState({
    email: 'frederick.jerde@example.com',
    firstName: 'Frederick',
    lastName: 'Jerde',
    address: '1600 Pennsylvania Avenue NW',
    apartment: '',
    city: 'Washington',
    state: 'DC',
    zipCode: '20500-0005',
    country: 'United States'
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCompleteOrder = () => {
    // Navigate to thank you page
    navigate('/shopify-thank-you')
  }

  // Check if simulation popup has been shown in this session
  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('shopify-simulation-popup-seen')
    if (!hasSeenPopup) {
      setShowSimulationPopup(true)
    }
  }, [])

  const handleClosePopup = () => {
    setShowSimulationPopup(false)
    sessionStorage.setItem('shopify-simulation-popup-seen', 'true')
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Simulation Info Popup */}
      {showSimulationPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button
              onClick={handleClosePopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">This is a Simulation</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  This checkout experience is a demonstration of how MomentScience can integrate into your e-commerce flow. 
                  Real implementation might differ slightly based on your settings and style preferences.
                </p>
                <p className="text-sm text-gray-700 font-medium">
                  Click <span className="text-blue-600 font-semibold">"Complete order"</span> to proceed to the thank-you screen where you can view different offer placement options.
                </p>
              </div>
            </div>

            <button
              onClick={handleClosePopup}
              className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-md font-medium text-sm hover:bg-blue-700 transition-colors mt-4"
            >
              Got it, continue
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/shopify-demo" className="flex items-center text-gray-600 hover:text-gray-800 transition-colors">
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Back to Shopify Demo Home</span>
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">MomentScience Swag Store</h1>
            <div className="w-32"></div> {/* Spacer for better alignment */}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left Column - Checkout Form */}
          <div className="px-6 py-8 lg:px-12 lg:py-12">
            <div className="max-w-lg mx-auto">

              {/* Contact Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Contact</h2>
                  <a href="#" className="text-sm text-blue-600 hover:text-blue-700">Sign in</a>
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email or mobile phone number"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                />
              </div>

              {/* Payment Section */}
              <div className="mb-8">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Payment</h2>
                <p className="text-sm text-gray-600 mb-4">All transactions are secure and encrypted.</p>
                
                <div className="bg-gray-100 border border-gray-300 rounded-md p-4 text-center">
                  <p className="text-gray-700 text-sm">Your order is free. No payment is required.</p>
                </div>
              </div>

              {/* Billing Address Section */}
              <div className="mb-8">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Billing address</h2>
                
                <div className="space-y-4">
                  <div>
                    <select 
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="United Kingdom">United Kingdom</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    />
                  </div>

                  <input
                    type="text"
                    name="address"
                    placeholder="Address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                  />

                  <input
                    type="text"
                    name="apartment"
                    placeholder="Apartment, suite, etc. (optional)"
                    value={formData.apartment}
                    onChange={handleInputChange}
                    className="w-full px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    />
                    <select 
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    >
                      <option value="">State</option>
                      <option value="CA">California</option>
                      <option value="NY">New York</option>
                      <option value="TX">Texas</option>
                      <option value="FL">Florida</option>
                      <option value="DC">District of Columbia</option>
                    </select>
                    <input
                      type="text"
                      name="zipCode"
                      placeholder="ZIP code"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                    />
                  </div>
                </div>
              </div>

              {/* Complete Order Button */}
              <button 
                onClick={handleCompleteOrder}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-md font-medium text-lg hover:bg-blue-700 transition-colors"
              >
                Complete order
              </button>

              {/* Privacy Policy Link */}
              <div className="mt-8">
                <a href="https://momentscience.com/end-user-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 underline">
                  Privacy policy
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="bg-gray-50 border-l border-gray-200 px-6 py-8 lg:px-12 lg:py-12">
            <div className="max-w-lg mx-auto">
              
              {/* Product Item */}
              <div className="mb-8">
                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-white border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=200&h=200&fit=crop" 
                        alt="MomentScience Pillow"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="absolute -top-2 -right-2 bg-gray-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                      1
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">MomentScience Pillow</h3>
                    <p className="text-sm text-gray-600 font-semibold">FREE</p>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="border-t border-gray-300 pt-6">
                <div className="flex justify-between items-center text-lg">
                  <span className="font-medium text-gray-900">Total</span>
                  <div className="text-right">
                    <span className="text-sm text-gray-600 mr-2">USD</span>
                    <span className="text-2xl font-bold text-gray-900">$0.00</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Floating flow helper */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {showFlowHint && (
          <div className="w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-blue-200 bg-white p-4 shadow-xl">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Info className="h-4 w-4" />
                <p className="text-sm font-semibold">Flow tip</p>
              </div>
              <button
                type="button"
                onClick={() => setShowFlowHint(false)}
                className="text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Close flow tip"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-700">
              Click <span className="font-semibold">Complete order</span> to continue to the Thank You page and see the offer placements.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowFlowHint((previous) => !previous)}
          className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-blue-700"
          aria-label="Toggle checkout flow help"
        >
          <Info className="h-4 w-4" />
          Flow help
        </button>
      </div>
    </div>
  )
}

export default ShopifyCheckoutPage